import {
  ClickAwayListener,
  createStyles,
  LinearProgress,
  MenuItem,
  Paper,
  Popper,
  Theme,
  WithStyles,
  withStyles,
} from '@freighthub/silhouette-core'
import { debounce, includes, intersection } from 'lodash-es'
import { action, observable } from 'mobx'
import { inject, observer } from 'mobx-react'
import { Component, KeyboardEvent, ReactElement } from 'react'
import { LocalisationStore } from 'utils/localisation'

import { Address } from '../Locations/LocationAddress'
import { isPort } from '../Locations/locationsUtils'
import { GooglePlace } from '../Locations/Place'
import { Port } from '../Locations/Port'
import { PlaceAutocompleteSuggestions } from './PlaceAutocompleteSuggestions'
import { PlacesSearchType } from './PlaceService'

const jssPlaceAutocomplete = (theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
    },
    input: {
      fontSize: theme.typography.body2.fontSize,
      boxSizing: 'border-box',
      border: 'none',
      boxShadow: 'none',
      outline: 'none',
      background: 'transparent',
      width: '100%',
      padding: '0 15px',
      lineHeight: theme.spacing(5),
      height: '40px',
    },
    progress: {
      position: 'absolute',
      width: '100%',
    },
    paper: {
      position: 'relative',
      borderRadius: '2px',
    },
    menu: {
      padding: 0,
      zIndex: 100,
    },
    menuPaper: {
      borderTopLeftRadius: '1px',
      borderTopRightRadius: '1px',
    },
    error: {
      color: theme.palette.error.main,
      borderBottom: `2px solid ${theme.palette.error.main}`,
      background: theme.palette.common.white,
      position: 'absolute',
      width: '100%',
      top: '100%',
      left: '0',
      marginTop: '2px',
      zIndex: 10,
    },
  })

interface PlacesData {
  ports: Port[]
  addresses: Address[]
}

const combinePlacesForSuggestions = (places: PlacesData) => {
  let suggestions = []
  const air = places.ports.filter((aPort) => includes(aPort.types, 'air'))
  const seaRail = places.ports.filter((aPort) => intersection(aPort.types, ['sea', 'rail']).length > 0)
  if (seaRail && seaRail.length) {
    // @ts-expect-error TS(2769) FIXME: No overload matches this call.
    suggestions = suggestions.concat(seaRail.slice(0, 3))
  }
  if (air && air.length) {
    // @ts-expect-error TS(2769) FIXME: No overload matches this call.
    suggestions = suggestions.concat(air.slice(0, 3))
  }
  if (places.addresses && places.addresses.length) {
    // @ts-expect-error TS(2769) FIXME: No overload matches this call.
    suggestions = suggestions.concat(places.addresses.slice(0, 3))
  }

  return suggestions
}

const keys = {
  RETURN: 13,
  UP: 38,
  DOWN: 40,
}

interface IPlaceAutocompleteInject {
  localisationStore: LocalisationStore
}

interface IPlaceAutocompleteProps
  extends Partial<IPlaceAutocompleteInject>,
    WithStyles<typeof jssPlaceAutocomplete, false> {
  filter: 'pol' | 'pod'
  label: string
  value: string
  name: 'origin' | 'destination'
  error: string | ReactElement
  types: PlacesSearchType[]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- // TODO: Replace any with a suitable type
  onSelect: (place: any) => void
  // TODO: will need to type the return value of search and details
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- // TODO: Replace any with a suitable type
  search: (typesArray: PlacesSearchType[], searchText: string, filter) => Promise<any>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- // TODO: Replace any with a suitable type
  details: (placeId: string) => Promise<any>
}

export class LeanPlaceAutocomplete extends Component<IPlaceAutocompleteProps> {
  container

  @observable
  searchText: string = this.props.value || ''
  @observable
  focusIndex = 0
  @observable
  open = false
  @observable
  focused = false
  @observable
  isMouseDown = false // if click on item, prevents action on blur
  @observable
  searching = false
  @observable
  showProgress = false // show visually immediately after any change
  suggestions = observable.shallowArray([])

  private debouncedSearch: Function
  private lastSearchId = 0 // to make sure only the last suggestions to show

  constructor(props) {
    super(props)
    this.debouncedSearch = debounce(this.search, 300)
  }

  UNSAFE_componentWillReceiveProps(nextProps: IPlaceAutocompleteProps) {
    this.searchText = nextProps.value || ''
  }

  @action
  search = () => {
    if (!this.searchText) {
      return
    }
    this.searching = true
    this.lastSearchId++

    // promise with "don't care" cancelation logic
    ;((lastSearchId) => {
      this.props.search(this.props.types, this.searchText, this.props.filter).then((places) => {
        if (!this.searchText || lastSearchId !== this.lastSearchId) {
          return
        }
        this.suggest(places)
      })
    })(this.lastSearchId)
  }

  @action
  suggest = (places) => {
    this.showProgress = false
    const combined = combinePlacesForSuggestions(places)
    this.suggestions.replace(combined)
    this.focusIndex = 0
    if (!this.focused && this.suggestions.length) {
      setTimeout(() => this.autoSelect(), 0)
    }
    this.open = true
    this.searching = false
  }

  @action
  setPlace(place: GooglePlace | Port) {
    if (isPort(place)) {
      this.searchText = place.name
      this.props.onSelect({
        country: '',
        googplace_types: place.types,
        ...place,
      })
    } else {
      this.searchText = place.description
      this.props.details(place.place_id).then(({ latitude, longitude, country }) => {
        this.props.onSelect({
          ...place,
          googplace_types: place.types,
          location: { lat: latitude, lng: longitude },
          types: ['address'],
          country,
        })
      })
    }
  }

  @action
  select(index: number) {
    this.open = false
    this.isMouseDown = false
    if (this.suggestions.length === 0) {
      return
    }
    const place = this.suggestions[index]
    this.setPlace(place)
    this.suggestions.replace([])
  }

  @action
  onChange = (e) => {
    this.searchText = e.target.value
    this.open = false
    if (this.searchText) {
      this.showProgress = true
      this.debouncedSearch()
    } else {
      this.props.onSelect(null)
      this.showProgress = false
    }
  }

  @action
  onSelect = (index: number) => {
    if (index === -1) {
      index = 0
    }
    this.select(index)
  }

  @action
  onBlur = () => {
    if (this.isMouseDown) {
      return
    }
    this.focused = false
    if (this.searching && this.suggestions.length === 0) {
      return null
    }
    this.autoSelect()
  }

  @action
  autoSelect = () => {
    if (this.suggestions.length === 0) {
      return
    }
    if (this.open) {
      this.select(this.focusIndex)
    }
  }

  @action
  onFocus = () => {
    this.focused = true
  }

  @action
  onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.which === keys.RETURN && (this.open || this.searching)) {
      e.preventDefault()
    }

    if (!this.open) {
      return
    }
    switch (e.which) {
      case keys.UP:
        e.preventDefault()
        this.focusIndex = Math.max(this.focusIndex - 1, 0)
        break
      case keys.DOWN:
        e.preventDefault()
        this.focusIndex = Math.min(this.focusIndex + 1, this.suggestions.length - 1)
        break
      case keys.RETURN:
        this.select(this.focusIndex)
        break
      default:
        return null
    }
  }

  onMouseDown = () => {
    this.isMouseDown = true
  }

  onMouseLeave = () => {
    this.isMouseDown = false
  }

  render() {
    const { classes } = this.props
    const width = this.container && this.container.clientWidth
    // important to acces focusIndex observable value, in order to rerender popper
    const { focusIndex } = this
    return (
      <div
        ref={(r) => {
          this.container = r
        }}
        className={classes.root}
      >
        <Paper className={classes.paper} elevation={1}>
          <input
            autoComplete="off"
            required
            name={this.props.name}
            placeholder={this.props.label}
            className={classes.input}
            value={this.searchText}
            onChange={this.onChange}
            onBlur={this.onBlur}
            onFocus={this.onFocus}
            onKeyDown={this.onKeyDown}
          />
          {this.showProgress && <LinearProgress className={classes.progress} />}
          {this.props.error ? <span className={classes.error}>{this.props.error}</span> : null}
        </Paper>

        <Popper
          open={this.open}
          anchorEl={this.container}
          className={classes.menu}
          placement="bottom-start"
          modifiers={[
            {
              name: 'flip',
              enabled: false,
            },
            {
              name: 'preventOverflow',
              enabled: true,
              options: {
                boundariesElement: 'window',
              },
            },
          ]}
          disablePortal
        >
          {() => (
            <ClickAwayListener onClickAway={this.autoSelect}>
              <Paper
                style={{ width }}
                className={classes.menuPaper}
                onMouseDown={this.onMouseDown}
                onMouseLeave={this.onMouseLeave}
              >
                {this.suggestions.length ? (
                  <PlaceAutocompleteSuggestions
                    suggestions={this.suggestions}
                    focusIndex={focusIndex}
                    onSelect={this.onSelect}
                    types={this.props.types}
                  />
                ) : (
                  // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
                  <MenuItem>{this.props.localisationStore.translate('quote_request-route-no_matches')}</MenuItem>
                )}
              </Paper>
            </ClickAwayListener>
          )}
        </Popper>
      </div>
    )
  }
}

const injector = inject(
  (ctx: IPlaceAutocompleteInject): IPlaceAutocompleteInject => ({
    localisationStore: ctx.localisationStore,
  })
)

export const PlaceAutocomplete = withStyles(jssPlaceAutocomplete)(injector(observer(LeanPlaceAutocomplete)))
